import { ReactNode, FC } from 'react';
import {
    AppBar,
    Avatar,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    AdminPanelSettings as AdminIcon,
    Logout as LogoutIcon,
    LightMode as LightIcon,
    DarkMode as DarkIcon,
    SettingsBrightness as SystemIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useGetProfileQuery, useLogoutMutation } from '@/store/apiSlice';
import { isLocale } from '@/i18n/i18n-util';
import { useColorMode } from '@/theme/ColorMode';

const Header: FC<{ title: string; children?: ReactNode }> = ({ title, children }) => {
    const { LL, locale, setLocale } = useI18nContext();
    const navigate = useNavigate();
    const { preference, cycle } = useColorMode();

    const isHomePath = useMatch({ path: '/', end: true });
    const isAdminPath = useMatch('/admin');

    const { data: user, error: userError } = useGetProfileQuery();
    const [logout, { isLoading: loggingOut }] = useLogoutMutation();

    const isAuthenticated = !!user && !userError;

    const handleLogout = async () => {
        try {
            const result = await logout().unwrap();
            if (result.logoutUrl) {
                window.location.href = result.logoutUrl;
            } else {
                window.location.href = '/';
            }
        } catch (error) {
            console.error('Logout failed:', error);
            window.location.href = '/';
        }
    };

    const appearanceLabel =
        preference === 'light'
            ? LL.themeLight()
            : preference === 'dark'
              ? LL.themeDark()
              : LL.themeSystem();

    const AppearanceIcon =
        preference === 'light' ? LightIcon : preference === 'dark' ? DarkIcon : SystemIcon;

    // Ghost treatment so the controls sit on the brand gradient without
    // competing with the wordmark. Tinted for whichever text colour the
    // configured brand pair needs.
    const ghostButton = (theme: { brand: { backgrounds: Record<string, string> } }) => ({
        color: 'inherit',
        backgroundColor: theme.brand.backgrounds.onBrandGhost,
        border: `1px solid ${theme.brand.backgrounds.onBrandBorder}`,
        '&:hover': { backgroundColor: theme.brand.backgrounds.onBrandGhostHover },
    });

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={(theme) => ({
                borderRadius: 0,
                color: theme.brand.onBrand,
                // The brand gradient carries the bar on its own; overlaying a
                // highlight only muddies it.
                backgroundImage: theme.brand.gradients.primary,
                borderBottom: `1px solid ${theme.brand.backgrounds.onBrandBorder}`,
            })}
        >
            <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, gap: 1, px: { xs: 2, sm: 3 } }}>
                {!isHomePath && (
                    <IconButton
                        onClick={() => navigate('/')}
                        aria-label={LL.home()}
                        sx={[ghostButton, { mr: 0.5 }]}
                    >
                        <BackIcon />
                    </IconButton>
                )}

                {/* Brand mark: a small tile carrying the app initial. */}
                <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}
                >
                    <Avatar
                        src="/favicon.png"
                        alt=""
                        aria-hidden
                        // Decorative: the title beside it already names the site.
                        // Avatar falls back to the initial if the icon is missing.
                        sx={(theme) => ({
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            display: { xs: 'none', sm: 'flex' },
                            fontWeight: 900,
                            fontSize: '1rem',
                            color: 'inherit',
                            backgroundColor: theme.brand.backgrounds.onBrandGhostHover,
                            border: `1px solid ${theme.brand.backgrounds.onBrandBorderStrong}`,
                            backdropFilter: theme.brand.effects.backdropBlur,
                            // A favicon is artwork, not a portrait: fit the whole
                            // mark inside the circle instead of cropping it.
                            '& .MuiAvatar-img': { objectFit: 'contain', padding: '3px' },
                        })}
                    >
                        {title.trim().charAt(0).toUpperCase() || 'I'}
                    </Avatar>
                    <Typography
                        variant="h6"
                        component="div"
                        noWrap
                        sx={{
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '1.05rem', sm: '1.2rem' },
                        }}
                    >
                        {title}
                    </Typography>
                </Stack>

                {children}

                <FormControl size="small" sx={{ minWidth: { xs: 92, sm: 116 } }}>
                    <Select
                        id="lang-select"
                        aria-label={LL.language()}
                        // Controlled by the active locale: a language resolved from
                        // server settings now shows up here instead of always "en".
                        value={locale}
                        onChange={(e) => {
                            const val = String(e.target.value);
                            if (!isLocale(val)) return;
                            localStorage.setItem('lang', val);
                            document.documentElement.lang = val;
                            // All locales are preloaded, so switch in place instead
                            // of reloading the whole application.
                            setLocale(val);
                        }}
                        sx={(theme) => ({
                            color: 'inherit',
                            backgroundColor: theme.brand.backgrounds.onBrandGhost,
                            fontWeight: 600,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.brand.backgrounds.onBrandBorder,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.brand.backgrounds.onBrandBorderStrong,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'currentColor',
                            },
                            '& .MuiSelect-icon': { color: 'inherit', opacity: 0.75 },
                        })}
                    >
                        <MenuItem value="en">{LL.languages.en()}</MenuItem>
                        <MenuItem value="de">{LL.languages.de()}</MenuItem>
                    </Select>
                </FormControl>

                <Tooltip title={LL.switchAppearance({ mode: appearanceLabel })}>
                    <IconButton
                        onClick={cycle}
                        aria-label={LL.switchAppearance({ mode: appearanceLabel })}
                        sx={ghostButton}
                    >
                        <AppearanceIcon />
                    </IconButton>
                </Tooltip>

                {!isAdminPath && isAuthenticated && user?.isAdmin && (
                    <Tooltip title={LL.admin()}>
                        <IconButton
                            onClick={() => navigate('/admin')}
                            aria-label={LL.admin()}
                            sx={ghostButton}
                        >
                            <AdminIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {isAuthenticated ? (
                    <Tooltip title={LL.logout()}>
                        <span>
                            <IconButton
                                onClick={handleLogout}
                                disabled={loggingOut}
                                aria-label={LL.logout()}
                                sx={ghostButton}
                            >
                                <LogoutIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
            </Toolbar>
        </AppBar>
    );
};

export default Header;
